import React, { Children, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faUser } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'gatsby';
import grid from '../../design-language/3rd-party/flexboxgrid.module.css';
import LayoutPageWrapper from '../LayoutPageWrapper';
import CTA from '../CTA';
import Logo from '../Logo';
import Heading from '../Heading';
import Loading from '../Loading';

import styles from './index.module.css';

const RenderControlsLoggedOut = () => (
  <nav className={classnames(styles.headerControls, styles.loggedOutMenu)}>
    <CTA href="/login" size="Small" title="Log in" importance="Secondary">Log in</CTA>
    <CTA href="/signup" size="Small" title="Sign up" importance="Primary">Sign up</CTA>
  </nav>
);

const HeaderColumn = ({ children, heading }) => (
  <div className={classnames(grid.colXs6, grid.colSm4)}>
    <Heading type="h6" size="XSmall">{heading}</Heading>
    <ul className={styles.headerLinks}>
      {Children.map(children, child => <li>{child}</li>)}
    </ul>
  </div>
)

const RenderMoreLinks = ({ setActiveMoreMenu }) => (
  <nav className={styles.loggedInMenuMoreWrapper}>
    <LayoutPageWrapper>
      <div className={styles.loggedInMenuMore}>
        <div className={styles.loggedInMenuMoreTop}>
          <Link to="/" className={styles.headerLogo}>
            <Logo />
          </Link>

          <div className={styles.loggedInMenuLess}>
            <CTA importance="Link" size="Small" onClick={() => setActiveMoreMenu(false)}>
              <FontAwesomeIcon
                icon={faBars}
                size="sm"
              />
              <span>Less</span>
            </CTA>
          </div>
        </div>
        <div className={classnames(styles.loggedInMenuMoreContent, grid.row)}>
          <HeaderColumn heading="User">
            <a href="/tests" title="Web Tests">Web Tests</a>
            <a href="/mobile-tests" title="Mobile Tests">Mobile Tests</a>
            <a href="results" title="Results">Results</a>
            <a href="/scheduler" title="Scheduler">Scheduler</a>
            <a href="/backups" title="Backups">Backups</a>
            <a href="/drive" title="Drive">Drive</a>
            <a href="/activity" title="Activity">Activity</a>
            <a href="https://endtest.io/guides/docs/how-to-create-web-tests/" title="Documentation">Documentation</a>
          </HeaderColumn>
          <HeaderColumn heading="Product">
            <a href="/why-endtest" title="Why Endtest?">Why Endtest?</a>
            <a href="/enterprise" title="Enterprise">Enterprise</a>
            <a href="/security" title="Security">Security</a>
            <a href="/customer-service" title="Customer Service">Customer Service</a>
            <a href="/pricing.htm" title="Pricing">Pricing</a>
            <a href="/endtest-demo" title="Endtest Demo">Endtest Demo</a>
          </HeaderColumn>
          <HeaderColumn heading="Components">
            <a href="https://endtest.io/guides/docs/how-to-create-web-tests/" title="Web Tests">Web Tests</a>
            <a href="https://endtest.io/guides/docs/how-to-create-mobile-tests/" title="Mobile Tests">Mobile Tests</a>
            <a href="https://endtest.io/guides/docs/how-to-use-the-scheduler/" title="Scheduler">Scheduler</a>
            <a href="https://endtest.io/guides/docs/computer-vision/" title="Computer Vision">Computer Vision</a>
            <a href="https://endtest.io/guides/docs/automatic-backup/" title="Backups">Backups</a>
            <a href="https://endtest.io/guides/docs/drive/" title="Drive">Drive</a>
            <a href="https://endtest.io/guides/docs/how-to-test-emails/" title="Mailbox">Mailbox</a>
          </HeaderColumn>
          <HeaderColumn heading="Resources">
            <a href="/documentation" title="Documentation">Documentation</a>
            <a href="/faq" title="FAQ">FAQ</a>
            <a href="/subprocessors" title="Subprocessors">Subprocessors</a>
            <a href="https://endtest.io/guides/docs/how-to-use-the-endtest-api/" title="API">API</a>
            <a href="https://endtest.io/guides/docs/self-healing-tests/" title="Machine Learning">Machine Learning</a>
          </HeaderColumn>
          <HeaderColumn heading="Company">
            <a href="/about-us" title="About Us">About Us</a>
            <a target="_blank"  title="Blog" href="https://endtest.io/guides/blog/2020/12/11/5-myths-about-codeless-automation/">Blog</a>
            <a href="/media-kit" title="Media Kit">Media Kit</a>
          </HeaderColumn>
        </div>
      </div>
    </LayoutPageWrapper>
  </nav>
)

const RenderControlsLoggedIn = ({ username, activeUserDropdown, setActiveUserDropdown, setActiveMoreMenu }) => (
  <>
    <div className={styles.headerLoggedInMenuWrapper}>
      <nav className={styles.headerLoggedInMenu}>
        <a href="/tests"><span>Web Tests</span></a>
        <a href="/mobile-tests"><span>Mobile Tests</span></a>
        <a href="results"><span>Results</span></a>
        <a href="/scheduler"><span>Scheduler</span></a>
        <a href="/backups"><span>Backups</span></a>
        <a href="/drive"><span>Drive</span></a>
        <a href="/activity"><span>Activity</span></a>
        <a href="/documentation"><span>Documentation</span></a>
      </nav>
      <div className={styles.headerMenuMore}>
        <CTA importance="Link" size="Small" onClick={() => setActiveMoreMenu(true)}>
          <FontAwesomeIcon
            icon={faBars}
            size="sm"
          />
          <span>More</span>
        </CTA>
      </div>
    </div>
    <nav className={classnames(styles.headerControls, styles.loggedInMenu)}>
      <CTA importance="Link" size="Small" onClick={() => setActiveUserDropdown(!activeUserDropdown)}>
        <span>{username}</span>
        <FontAwesomeIcon
          icon={faUser}
          size="sm"
        />
      </CTA>
      <ul className={classnames(styles.userDropdown, { [styles.isActive]: activeUserDropdown })}>
        <li><a href="/settings">Settings</a></li>
        <li><a href="/logout">Logout</a></li>
      </ul>
    </nav>
  </>
);

const Header = ({ isCheckingUserStatus, user }) => {
  const { isLoggedIn, username } = user;
  const [activeUserDropdown, setActiveUserDropdown] = useState(false);
  const [activeMoreMenu, setActiveMoreMenu] = useState(false);

  return (
    <header className={classnames(styles.header, { [styles.headerLoggedIn]: isLoggedIn })}>
      <LayoutPageWrapper>
        <div className={styles.headerContent}>
          <a href="/" className={styles.headerLogo}>
            <Logo />
          </a>
          {isCheckingUserStatus ?
            <Loading /> :
            (isLoggedIn ?
              <RenderControlsLoggedIn
                username={username}
                activeUserDropdown={activeUserDropdown} setActiveUserDropdown={setActiveUserDropdown}
                setActiveMoreMenu={setActiveMoreMenu}
              /> :
              <RenderControlsLoggedOut/>
            )
          }
        </div>
      </LayoutPageWrapper>
      {activeMoreMenu && <RenderMoreLinks setActiveMoreMenu={setActiveMoreMenu}/> }
    </header>
  );
}

Header.propTypes = {
  isCheckingUserStatus: PropTypes.bool.isRequired,
  user: PropTypes.shape(
    {
      isLoggedIn: PropTypes.bool.isRequired,
      username: PropTypes.string,
    },
  ),
};

Header.defaultProps = {
  user: {
    username: null,
  },
};

export default Header;
